import moment from 'moment';
import { saveToIndexedDb } from '·/utils/indexedDbFileStore';

/**
 * Gantt Engine Mixin
 *
 * Core gantt chart logic: rendering, events, zoom, scroll, pan, columns,
 * dependencies rendering, bar styles, tooltips, formatting, screenshot, fullscreen.
 *
 * Used by gantt.vue which adds project-specific data handling (API, data mapping, etc.)
 */
export default {
  data() {
    return {
      // UI State
      containerHeight: null,
      leftPanelWidth: 700,
      splitterDragging: false,
      splitterStartX: 0,
      splitterStartWidth: 0,
      showSettingsMenu: false,
      showViewsMenu: false,
      viewsMenuPosition: { x: 0, y: 0 },
      showInfoTooltip: false,
      selectedRowId: null,
      timelineScrollLeft: 0,
      timelineViewportWidth: 0,

      // Sorting
      sortField: 'code',
      sortDirection: 'asc',

      // Column Context Menu
      columnContextMenu: {
        visible: false,
        x: 0,
        y: 0
      },

      // Columns (matching original exactly)
      columns: [
        { field: 'code', textKey: 'code', text: 'Code', width: 160, minWidth: 50, isTree: true, visible: true },
        { field: 'name', textKey: 'name', text: 'Name', width: 250, minWidth: 100, visible: true },
        { field: 'weight', textKey: 'weight', text: 'Weight', width: 90, minWidth: 60, visible: true },
        { field: 'current_start_date', textKey: 'gantt_planned_start', text: 'Planned Start', width: 145, minWidth: 110, visible: true },
        { field: 'current_end_date', textKey: 'gantt_planned_end', text: 'Planned End', width: 145, minWidth: 110, visible: true },
        { field: 'startDate', textKey: 'gantt_expected_start', text: 'Expected Start', width: 145, minWidth: 110, visible: true },
        { field: 'endDate', textKey: 'gantt_expected_end', text: 'Expected End', width: 145, minWidth: 110, visible: true },
        { field: 'gantt_real_start_date', textKey: 'gantt_real_start', text: 'Actual Start', width: 145, minWidth: 110, visible: true },
        { field: 'gantt_real_end_date', textKey: 'gantt_real_end', text: 'Actual End', width: 145, minWidth: 110, visible: true },
        { field: 'duration', textKey: 'duration', text: 'Duration', width: 100, minWidth: 60, visible: true },
        { field: 'status', textKey: 'status', text: 'Status', width: 150, minWidth: 80, visible: true },
        { field: 'percentDone', textKey: 'gantt_progress_real', text: 'Progress', width: 70, minWidth: 50, visible: true },
        { field: 'latestPlanningProgress', textKey: 'gantt_progress_planned', text: 'Latest planning progress', width: 70, minWidth: 50, visible: true },
        { field: 'successors', textKey: 'successors', text: 'Successors', width: 150, minWidth: 80, visible: true },
        { field: 'predecessors', textKey: 'predecessors', text: 'Predecessors', width: 150, minWidth: 80, visible: true }
      ],
      columnDragging: {
        active: false,
        sourceIndex: null,
        targetIndex: null,
        startX: 0,
        currentX: 0,
        startOffsetX: 0,
        headerTop: 0,
        columnWidth: 0,
        hasMoved: false,
        justFinishedDrag: false
      },

      // Zoom
      zoomLevel: 0,
      zoomLevels: [
        { name: 'years', dayWidth: 0.5, headerUnits: ['year'] },
        { name: 'years-detail', dayWidth: 1, headerUnits: ['year', 'quarter'] },
        { name: 'quarters', dayWidth: 1.5, headerUnits: ['year', 'quarter'] },
        { name: 'quarters-detail', dayWidth: 2.25, headerUnits: ['year', 'month'] },
        { name: 'months', dayWidth: 3, headerUnits: ['year', 'month'] },
        { name: 'months-detail', dayWidth: 5, headerUnits: ['year', 'month'] },
        { name: 'weeks', dayWidth: 8, headerUnits: ['year', 'month', 'week'] },
        { name: 'weeks-detail', dayWidth: 15, headerUnits: ['year', 'month', 'week'] },
        { name: 'days', dayWidth: 25, headerUnits: ['year', 'month', 'day'] }
      ],

      // Timeline dates
      projectStartDate: null,
      projectEndDate: null,
      timelineStartDate: null,
      timelineEndDate: null,

      // Row Settings
      rowHeight: 40,
      barMargin: 7,

      // Baselines
      baselinesConfig: {
        'main-bar': { visible: true, labelKey: 'gantt_record_progress' },
        'baseline-0': { visible: false, labelKey: 'gantt_baseline' },
        'baseline-1': { visible: false, labelKey: 'latest_planning' },
        'baseline-2': { visible: false, labelKey: 'progress_inspected_label' }
      },

      // Features
      enableDependencies: true,
      enableCriticalPaths: false,
      filterCriticalOnly: false,
      filterCriticalFlat: false,
      filterLongestPath: false,
      showCriticalMenu: false,
      isCapturing: false,

      // Tooltip
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        type: null,
        data: null
      },

      // Colors (matching original Bryntum theme)
      colors: {
        activity: [
          { normal: "#B8C2CC", light: "#E1E5E8" },  // 0 assigned
          { normal: "#3490dc", light: "#85B9F2" },  // 1 running
          { normal: "#F6993F", light: "#FFC28F" },  // 2 paused
          { normal: "#e3342f", light: "#F2807C" },  // 3 issue
          { normal: "#6574CD", light: "#A3AFE9" },  // 4 done
          { normal: "#9561E2", light: "#C5A4F5" },  // 5 reopened
          { normal: "#38C172", light: "#7FDEAA" },  // 6 closed
          { normal: "#fc8a18", light: "#FFBC66" },  // 7 delayed_start
          { normal: "#f9c74f", light: "#fad06b" }   // 8 delayed
        ],
        assignment: [
          { normal: "#38C172", light: "#7FDEAA" },  // 0 OK
          { normal: "#F6993F", light: "#FFC28F" },  // 1 PENDING
          { normal: "#e3342f", light: "#F2807C" }   // 2 REJECTED
        ],
        defaults: {
          scheduled: { normal: "#387bd9", light: "#5393ed" },
          original: { normal: "#5dcff5", light: "#87ddfa" },
          real: { normal: "#6ea8fa", light: "#8fbbf7" },
          inspected: { normal: "#ff5e59", light: "#e3342f" },
          gray: { normal: "#B8C2CC", light: "#E1E5E8" },
          yellow: { normal: "#F8C94D", light: "#F9E6A0" },
          critical: { normal: "#cc2d29", light: "#E0817E" }
        }
      },

      // Labels (matching original)
      labels: {
        activity: [
          'activity_status_assigned',
          'activity_status_running',
          'activity_status_paused',
          'activity_status_issue',
          'activity_status_done',
          'activity_status_reopened',
          'activity_status_closed',
          'activity_status_delayed_start',
          'activity_status_delayed'
        ],
        assignment: ['ok', 'pending', 'rejected']
      },

      // Dependency Types
      dependencyTypes: ['CC', 'CF', 'FC', 'FF'],

      // Max expanded parent nodes (FIFO: oldest collapses when limit exceeded)
      maxExpandedParents: 66,
      expandedParentIds: [],

      // Virtual scrolling
      visibleScrollTop: 0,
      viewportHeight: 600,

      // Version counters para invalidar computed properties selectivamente
      visibleVersion: 0,
      depPathsVersion: 0,
    };
  },

  computed: {
    currentZoom() {
      return this.zoomLevels[this.zoomLevel];
    },

    // Cached timestamp for dateToX — avoids creating moment objects on every call
    timelineStartMs() {
      if (!this.timelineStartDate) return 0;
      return this.timelineStartDate._isAMomentObject
        ? this.timelineStartDate.valueOf()
        : new Date(this.timelineStartDate).getTime();
    },

    totalTimelineDays() {
      if (!this.timelineStartDate || !this.timelineEndDate) return 0;
      const endMs = this.timelineEndDate._isAMomentObject
        ? this.timelineEndDate.valueOf()
        : new Date(this.timelineEndDate).getTime();
      return (endMs - this.timelineStartMs) / 86400000;
    },

    totalTimelineWidth() {
      if (!this.totalTimelineDays) return 1000;
      return Math.max(this.totalTimelineDays * this.currentZoom.dayWidth, 1000);
    },

    totalRowsHeight() {
      return this.sortedVisibleRows.length * this.rowHeight;
    },

    // Offset Y para posicionar rows dentro del content div sticky (compensa el scroll)
    contentScrollOffset() {
      return this.renderStartIndex * this.rowHeight;
    },

    visibleColumns() {
      return this.columns.filter(col => col.visible);
    },

    // Anchos de columnas de dependencias — se calcula una vez al cargar datos, no en cada render
    dependencyColumnWidths() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion;
      var self = this;
      // Build maps: taskId -> array of deps
      var succMap = {};
      var predMap = {};
      this.dependencies.forEach(function(d) {
        if (!succMap[d.from]) succMap[d.from] = [];
        succMap[d.from].push(d);
        if (!predMap[d.to]) predMap[d.to] = [];
        predMap[d.to].push(d);
      });

      var maxSuccW = 0;
      var maxPredW = 0;

      // Usamos flattenedRows (todos) en vez de sortedVisibleRows para no re-calcular al expandir/colapsar
      this.flattenedRows.forEach(function(row) {
        var succs = succMap[row.id] || [];
        var preds = predMap[row.id] || [];

        var succW = 0;
        succs.forEach(function(dep) {
          var code = self.getTaskCode(dep.to) || '';
          var type = self.dependencyTypes[dep.type] || '';
          var lagText = '';
          if (dep.lag !== 0) {
            lagText = ' ' + (dep.lag > 0 ? '+' : '') + (dep.lag + 1) + (dep.lagUnit || '');
          }
          var text = code + ' | ' + type + lagText;
          succW += (text.length * 5.5) + 18;
        });
        if (succW > maxSuccW) maxSuccW = succW;

        var predW = 0;
        preds.forEach(function(dep) {
          var code = self.getTaskCode(dep.from) || '';
          var type = self.dependencyTypes[dep.type] || '';
          var lagText = '';
          if (dep.lag !== 0) {
            lagText = ' ' + (dep.lag > 0 ? '+' : '') + (dep.lag + 1) + (dep.lagUnit || '');
          }
          var text = code + ' | ' + type + lagText;
          predW += (text.length * 5.5) + 18;
        });
        if (predW > maxPredW) maxPredW = predW;
      });

      return {
        successors: maxSuccW,
        predecessors: maxPredW
      };
    },

    totalColumnsWidth() {
      var self = this;
      return this.visibleColumns.reduce(function(sum, col) {
        return sum + self.getEffectiveColumnWidth(col);
      }, 0);
    },

    columnGhostStyle() {
      if (!this.columnDragging.active || !this.columnDragging.hasMoved) return {};
      const col = this.visibleColumns[this.columnDragging.sourceIndex];
      if (!col) return {};
      return {
        width: col.width + 'px',
        left: (this.columnDragging.currentX - this.columnDragging.startOffsetX) + 'px',
        top: this.columnDragging.headerTop + 'px'
      };
    },

    visibleRows() {
      // Se recalcula cuando cambia visibleVersion, dataVersion o filterCriticalOnly
      // eslint-disable-next-line no-unused-expressions
      this.visibleVersion; this.dataVersion;
      if (this.filterCriticalFlat) {
        // Solo actividades críticas hojas, sin jerarquía (plano), todas a nivel 0
        return this.flattenedRows
          .filter(row => row.isCriticalPath && (!row.children || row.children.length === 0))
          .map((row, idx) => {
            if (!row._flatLevel) row._flatLevel = row.level;
            return row;
          });
      }
      if (this.filterLongestPath) {
        // Longest Path: la ruta de dependencias más larga (por duración), plano sin jerarquía
        const lpIds = this._computeLongestPath();
        return this.flattenedRows
          .filter(row => lpIds.has(row.id) && (!row.children || row.children.length === 0));
      }
      return this.flattenedRows.filter(row => row.visible);
    },

    // Orden cacheado del árbol — solo se recalcula cuando cambia sortField/sortDirection o la data
    sortedFlatCache() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion; this.sortField; this.sortDirection;
      const sortedFlat = [];
      this.sortNodesRecursive(this.rawData, sortedFlat);
      return sortedFlat;
    },

    sortedVisibleRows() {
      // Solo filtra por visibilidad (O(n) filter) — NO re-ordena
      // eslint-disable-next-line no-unused-expressions
      this.visibleVersion; this.dataVersion;
      if (!this.sortField) return this.visibleRows;

      // Cuando hay filtro de crítico activo, usamos visibleRows (que ya filtra)
      if (this.filterCriticalOnly || this.filterCriticalFlat || this.filterLongestPath) {
        const visibleIds = new Set(this.visibleRows.map(r => r.id));
        return this.sortedFlatCache.filter(row => visibleIds.has(row.id));
      }
      return this.sortedFlatCache.filter(row => row.visible);
    },

    taskMap() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion;
      const map = {};
      this.flattenedRows.forEach(row => {
        map[row.id] = row;
      });
      return map;
    },

    // Mapas de dependencias pre-computados — O(1) lookup en vez de O(m) filter por celda
    successorMap() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion;
      const map = {};
      this.dependencies.forEach(d => {
        if (!map[d.from]) map[d.from] = [];
        map[d.from].push(d);
      });
      return map;
    },

    predecessorMap() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion;
      const map = {};
      this.dependencies.forEach(d => {
        if (!map[d.to]) map[d.to] = [];
        map[d.to].push(d);
      });
      return map;
    },

    // O(1) row index lookup — se recalcula solo cuando cambia la lista visible
    rowIndexMap() {
      // eslint-disable-next-line no-unused-expressions
      this.visibleVersion; this.dataVersion;
      const map = {};
      const rows = this.sortedVisibleRows;
      for (let i = 0; i < rows.length; i++) {
        map[rows[i].id] = i;
      }
      return map;
    },

    // Virtual scrolling: only render rows visible in viewport + large buffer.
    // Buffer of ±20 rows (800px) ensures rows stay pre-rendered during
    // panning gestures (where reactive updates are skipped for performance).
    renderStartIndex() {
      if (this.isCapturing) return 0;
      return Math.max(0, Math.floor(this.visibleScrollTop / this.rowHeight) - 20);
    },

    renderEndIndex() {
      if (this.isCapturing) return Math.min(this.sortedVisibleRows.length, Math.floor(16000 / this.rowHeight));
      const visibleCount = Math.ceil(this.viewportHeight / this.rowHeight);
      return Math.min(this.renderStartIndex + visibleCount + 40, this.sortedVisibleRows.length);
    },

    renderedRows() {
      return this.sortedVisibleRows.slice(this.renderStartIndex, this.renderEndIndex);
    },

    hasVisibleBaselines() {
      return Object.keys(this.baselinesConfig).some(key =>
        key.startsWith('baseline-') && this.baselinesConfig[key].visible
      );
    },

    nowLabelPosition() {
      const scrollLeft = this.timelineScrollLeft || 0;
      const viewportWidth = this.timelineViewportWidth || 800;
      const nowX = this.currentTimeX;
      const left = nowX - scrollLeft;
      // Show label when the line is within the viewport (with some margin)
      const isVisible = left >= -30 && left <= viewportWidth + 30;
      return { left, isVisible };
    },

    timeHeaders() {
      if (!this.timelineStartDate || !this.timelineEndDate) return [];

      const headers = [];
      const zoom = this.currentZoom;
      const start = moment(this.timelineStartDate);
      const end = moment(this.timelineEndDate);

      // Viewport-aware bounds for virtualizing high-frequency rows (week, day).
      // 2000px buffer on each side covers panning gestures where reactive state
      // isn't updated until the gesture ends.
      const scrollLeft = this.timelineScrollLeft || 0;
      const viewportWidth = this.timelineViewportWidth || 800;
      const buffer = 2000;
      const visibleStart = scrollLeft - buffer;
      const visibleEnd = scrollLeft + viewportWidth + buffer;

      // Year row (low cell count ~6 — always render all)
      if (zoom.headerUnits.includes('year')) {
        const yearRow = { unit: 'year', cells: [] };
        let current = moment(start).startOf('year');

        while (current.isBefore(end)) {
          const yearEnd = moment(current).endOf('year');
          const effectiveStart = moment.max(current, start);
          const effectiveEnd = moment.min(yearEnd, end);

          const x = this.dateToX(effectiveStart);
          const width = this.dateToX(effectiveEnd) - x;

          if (width > 0) {
            yearRow.cells.push({
              label: current.format('YYYY'),
              x,
              width
            });
          }
          current.add(1, 'year').startOf('year');
        }
        headers.push(yearRow);
      }

      // Quarter row (low cell count ~24 — always render all)
      if (zoom.headerUnits.includes('quarter')) {
        const quarterRow = { unit: 'quarter', cells: [] };
        let current = moment(start).startOf('quarter');

        while (current.isBefore(end)) {
          const quarterEnd = moment(current).endOf('quarter');
          const effectiveStart = moment.max(current, start);
          const effectiveEnd = moment.min(quarterEnd, end);

          const x = this.dateToX(effectiveStart);
          const width = this.dateToX(effectiveEnd) - x;

          if (width > 0) {
            quarterRow.cells.push({
              label: 'Q' + current.quarter(),
              x,
              width
            });
          }
          current.add(1, 'quarter').startOf('quarter');
        }
        headers.push(quarterRow);
      }

      // Month row (low cell count ~72 — always render all)
      if (zoom.headerUnits.includes('month')) {
        const monthRow = { unit: 'month', cells: [] };
        let current = moment(start).startOf('month');

        while (current.isBefore(end)) {
          const monthEnd = moment(current).endOf('month');
          const effectiveStart = moment.max(current, start);
          const effectiveEnd = moment.min(monthEnd, end);

          const x = this.dateToX(effectiveStart);
          const width = this.dateToX(effectiveEnd) - x;

          if (width > 0) {
            monthRow.cells.push({
              label: current.format('MMM'),
              x,
              width
            });
          }
          current.add(1, 'month').startOf('month');
        }
        headers.push(monthRow);
      }

      // Week row (VIRTUALIZED — only visible cells + buffer)
      if (zoom.headerUnits.includes('week')) {
        const weekRow = { unit: 'week', cells: [] };
        // Jump close to the first visible week instead of iterating from start
        const daysToVisible = Math.max(0, visibleStart / zoom.dayWidth);
        let current = moment(start).add(Math.floor(daysToVisible / 7) * 7, 'days').startOf('week');
        if (current.isBefore(start)) {
          current = moment(start).startOf('week');
        }

        while (current.isBefore(end)) {
          const weekEnd = moment(current).endOf('week');
          const effectiveStart = moment.max(current, start);
          const effectiveEnd = moment.min(weekEnd, end);

          const x = this.dateToX(effectiveStart);
          const width = this.dateToX(effectiveEnd) - x;

          // Past visible range — stop
          if (x > visibleEnd) break;

          if (width > 0) {
            weekRow.cells.push({
              label: 'S' + current.week(),
              x,
              width
            });
          }
          current.add(1, 'week').startOf('week');
        }
        headers.push(weekRow);
      }

      // Day row (VIRTUALIZED — only visible cells + buffer)
      // Uses native Date instead of moment for maximum speed (~200 iterations)
      if (zoom.headerUnits.includes('day')) {
        const dayRow = { unit: 'day', cells: [] };
        const dayWidth = zoom.dayWidth;
        const totalDays = end.diff(start, 'days');

        // Calculate first/last visible day index directly (skip iteration)
        const firstDay = Math.max(0, Math.floor(visibleStart / dayWidth));
        const lastDay = Math.min(Math.ceil(visibleEnd / dayWidth), totalDays);

        // Native Date: getDate() + setDate() is ~20x faster than moment.format('D')
        const nativeDate = new Date(start.valueOf());
        nativeDate.setDate(nativeDate.getDate() + firstDay);

        for (let i = firstDay; i < lastDay; i++) {
          dayRow.cells.push({
            label: String(nativeDate.getDate()),
            x: i * dayWidth,
            width: dayWidth
          });
          nativeDate.setDate(nativeDate.getDate() + 1);
        }
        headers.push(dayRow);
      }

      return headers;
    },

    timeHeaderHeight() {
      // Fixed height equivalent to 2 rows (28px * 2 = 56px)
      return 56;
    },

    gridLines() {
      if (!this.timelineStartDate || !this.timelineEndDate) return [];

      const lines = [];
      const zoom = this.currentZoom;
      const start = moment(this.timelineStartDate);
      const end = moment(this.timelineEndDate);

      // Grid lines are capped at month level to avoid too many lines
      let gridUnit = 'month';
      let majorUnit = 'year';

      if (zoom.name === 'days' || zoom.name === 'weeks') {
        gridUnit = 'month';
        majorUnit = 'year';
      } else if (zoom.name === 'months') {
        gridUnit = 'month';
        majorUnit = 'year';
      } else if (zoom.name === 'quarters') {
        gridUnit = 'quarter';
        majorUnit = 'year';
      } else {
        gridUnit = 'year';
        majorUnit = 'year';
      }

      // Solo generamos líneas dentro del viewport + buffer
      const scrollLeft = this.timelineScrollLeft || 0;
      const viewportWidth = this.timelineViewportWidth || 800;
      const buffer = 2000;
      const visibleStart = scrollLeft - buffer;
      const visibleEnd = scrollLeft + viewportWidth + buffer;

      let current = moment(start).startOf(gridUnit);
      while (current.isBefore(end)) {
        const x = Math.round(this.dateToX(current));

        // Solo añadimos líneas dentro del rango visible
        if (x >= visibleStart && x <= visibleEnd) {
          const isMajor = current.clone().startOf(majorUnit).isSame(current, 'day');
          lines.push({ x, isMajor });
        }
        current.add(1, gridUnit);
      }

      return lines;
    },

    currentTimeX() {
      return this.dateToX(moment());
    },

    projectStartX() {
      if (!this.projectStartDate) return 0;
      return this.dateToX(this.projectStartDate);
    },

    projectEndX() {
      if (!this.projectEndDate) return 0;
      return this.dateToX(this.projectEndDate);
    },

    dependencyPaths() {
      // eslint-disable-next-line no-unused-expressions
      this.depPathsVersion;
      return this._cachedDependencyPaths;
    },
  },

  created() {
    // Datos grandes como propiedades no-reactivas (evita que Vue observe 15K+ objetos)
    this.rawData = [];
    this.flattenedRows = [];
    this.dependencies = [];
    this.mappedActivities = {};
    this.criticalPathMode = 'calculated';

    // All panning state is non-reactive (plain JS properties).
    // This bypasses Vue's getter/setter overhead in the mousemove hot path.
    this._isSyncing = false;
    this._rafPanId = null;
    this._rafLeftPanId = null;
    this._rafSyncId = null;
    // Right panel panning
    this._isPanningActive = false;
    this._panStartX = 0;
    this._panStartY = 0;
    this._panStartScrollLeft = 0;
    this._panStartScrollTop = 0;
    // Left panel panning
    this._isLeftPanningActive = false;
    this._leftPanStartX = 0;
    this._leftPanStartY = 0;
    this._leftPanStartScrollLeft = 0;
    this._leftPanStartScrollTop = 0;
    // Bound handlers for document-level native listeners
    this._boundHandlePan = this.handlePan.bind(this);
    this._boundEndPan = this.endPan.bind(this);
    this._boundHandleLeftPan = this.handleLeftPan.bind(this);
    this._boundEndLeftPan = this.endLeftPan.bind(this);
    // Dependency paths cache (no-reactivo, se actualiza de forma asíncrona)
    this._cachedDependencyPaths = [];
    this._depPathsTimer = null;
    // Zoom throttling state
    this._zoomRafId = null;
    this._pendingZoom = null;
    // Column resize state (non-reactive for performance)
    this._colResizeActive = false;
    this._colResizeIndex = null;
    this._colResizeStartX = 0;
    this._colResizeStartWidth = 0;
    this._colResizeRafId = null;
    this._boundHandleColumnResize = this.handleColumnResize.bind(this);
    this._boundEndColumnResize = this.endColumnResize.bind(this);
  },

  methods: {

    // =========================================================================
    // Container / Layout
    // =========================================================================

    calculateContainerHeight() {
      if (this.$refs.ganttContainer) {
        const rect = this.$refs.ganttContainer.getBoundingClientRect();
        this.containerHeight = window.innerHeight - rect.top - 16;
      }
      if (this.$refs.rightBody) {
        this.viewportHeight = this.$refs.rightBody.clientHeight;
      }
    },

    // =========================================================================
    // Dependency paths (cálculo diferido para no bloquear interacciones)
    // =========================================================================

    // Longest Path: la cadena de dependencias más larga por duración acumulada
    // Incluye TODAS las actividades (no solo float=0)
    _computeLongestPath() {
      const MS_PER_DAY = 86400000;
      const leafTasks = this.flattenedRows.filter(r =>
        (!r.children || r.children.length === 0) && r.startDate && r.endDate
      );
      if (leafTasks.length === 0) return new Set();

      const taskMap = new Map();
      for (const t of leafTasks) taskMap.set(t.id, t);

      // Todas las dependencias entre leaf tasks
      const preds = new Map();
      for (const dep of this.dependencies) {
        if (!taskMap.has(dep.from) || !taskMap.has(dep.to)) continue;
        if (!preds.has(dep.to)) preds.set(dep.to, []);
        preds.get(dep.to).push(dep);
      }

      const getDurDays = (id) => {
        const t = taskMap.get(id);
        if (!t) return 1;
        return Math.max(1, Math.round((new Date(t.endDate) - new Date(t.startDate)) / MS_PER_DAY) + 1);
      };

      // DP: duración acumulada más larga terminando en cada tarea
      const pathDur = new Map();
      const drivingPred = new Map();
      const gpl = (id, vis = new Set()) => {
        if (pathDur.has(id)) return pathDur.get(id);
        if (vis.has(id)) return getDurDays(id);
        vis.add(id);
        let mx = 0, bp = null;
        for (const dep of (preds.get(id) || [])) {
          const pl = gpl(dep.from, vis);
          if (pl > mx) { mx = pl; bp = dep.from; }
        }
        const total = mx + getDurDays(id);
        pathDur.set(id, total);
        if (bp) drivingPred.set(id, bp);
        return total;
      };
      for (const t of leafTasks) gpl(t.id);

      // Encontrar la cadena más larga
      let maxDur = 0;
      for (const [, d] of pathDur) { if (d > maxDur) maxDur = d; }

      // Trazar hacia atrás
      const lpIds = new Set();
      for (const [id, d] of pathDur) {
        if (d === maxDur) {
          let c = id;
          while (c) {
            lpIds.add(c);
            c = drivingPred.get(c) || null;
          }
        }
      }

      return lpIds;
    },

    scheduleDependencyPathsUpdate() {
      if (this._depPathsTimer) clearTimeout(this._depPathsTimer);
      this._depPathsTimer = setTimeout(() => {
        this._depPathsTimer = null;
        // No computar si el usuario está panneando
        if (this._isPanningActive || this._isLeftPanningActive) {
          this.scheduleDependencyPathsUpdate();
          return;
        }
        this._computeDependencyPaths();
      }, 500);
    },

    _computeDependencyPaths() {
      if (!this.enableDependencies) {
        this._cachedDependencyPaths = [];
        return;
      }

      const rStart = this.renderStartIndex;
      const rEnd = this.renderEndIndex;
      const rowMap = this.rowIndexMap;
      // En modo filtrado (flat/hierarchy) mostramos todas las dependencias entre actividades visibles
      const isFiltered = this.filterCriticalFlat || this.filterCriticalOnly || this.filterLongestPath;

      const visibleDeps = this.dependencies.filter(dep => {
        const fromIdx = rowMap[dep.from];
        const toIdx = rowMap[dep.to];
        if (fromIdx === undefined || toIdx === undefined) return false;
        if (isFiltered) return true; // ambos endpoints están en la vista filtrada
        return (fromIdx >= rStart && fromIdx < rEnd) || (toIdx >= rStart && toIdx < rEnd);
      });

      const sourceGroups = {};
      visibleDeps.forEach(dep => {
        const key = `${dep.from}-${dep.type}`;
        if (!sourceGroups[key]) sourceGroups[key] = [];
        sourceGroups[key].push(dep);
      });

      const paths = [];
      Object.values(sourceGroups).forEach(group => {
        if (group.length === 1) {
          const result = this.calculateSingleDependencyPath(group[0]);
          if (result) paths.push(result);
        } else {
          const treePaths = this.calculateTreeDependencyPaths(group);
          paths.push(...treePaths);
        }
      });

      this._cachedDependencyPaths = paths.filter(p => p !== null);
      this.depPathsVersion++;
    },

    // =========================================================================
    // Date / Position calculations
    // =========================================================================

    // Uses cached timestamp to avoid moment.js overhead.
    // Called hundreds of times per render cycle (bars, baselines, dependencies, headers).
    dateToX(date) {
      const startMs = this.timelineStartMs;
      if (!startMs) return 0;
      const ms = date && date._isAMomentObject ? date.valueOf()
        : (date instanceof Date ? date.getTime() : new Date(date).getTime());
      return ((ms - startMs) / 86400000) * this.currentZoom.dayWidth;
    },

    getDuration(row) {
      if (!row.startDate || !row.endDate) return 0;
      return moment(row.endDate).diff(moment(row.startDate), 'days') + 1;
    },

    // =========================================================================
    // Expand / Collapse (with max open parent limit)
    // =========================================================================

    toggleExpand(row) {
      if (!row.expanded) {
        // Expanding: enforce max expanded parents limit (FIFO)
        if (row.children && row.children.length) {
          this.expandedParentIds.push(row.id);

          while (this.expandedParentIds.length > this.maxExpandedParents) {
            const oldestId = this.expandedParentIds.shift();
            if (oldestId !== row.id) {
              const oldestRow = this.taskMap[oldestId];
              if (oldestRow) {
                oldestRow.expanded = false;
              }
            }
          }
        }
        row.expanded = true;
      } else {
        // Collapsing: remove from tracking
        row.expanded = false;
        const idx = this.expandedParentIds.indexOf(row.id);
        if (idx !== -1) {
          this.expandedParentIds.splice(idx, 1);
        }
      }

      this.updateVisibility();

      this.$nextTick(() => {
        this.scrollToTask(row);
      });
    },

    updateVisibility() {
      const updateChildren = (nodes, parentVisible) => {
        for (const node of nodes) {
          node.visible = parentVisible;
          if (node.children && node.children.length) {
            updateChildren(node.children, parentVisible && node.expanded);
          }
        }
      };

      for (const node of this.rawData) {
        node.visible = true;
        if (node.children && node.children.length) {
          updateChildren(node.children, node.expanded);
        }
      }
      this.visibleVersion++;
      this.scheduleDependencyPathsUpdate();
    },

    // =========================================================================
    // Baselines
    // =========================================================================

    getVisibleBaselines(row) {
      if (!row.baselines || !row.baselines.length) return [];
      const visible = [];
      row.baselines.forEach((baseline, idx) => {
        if (this.baselinesConfig['baseline-' + idx] && this.baselinesConfig['baseline-' + idx].visible) {
          visible.push({ ...baseline, index: idx });
        }
      });
      return visible;
    },

    // =========================================================================
    // Task bar styling
    // =========================================================================

    getTaskBarStyle(row, rowIdx) {
      const startX = this.dateToX(row.startDate);
      const endX = this.dateToX(row.endDate);
      const mainBarHeight = this.hasVisibleBaselines ? 12 : 18;
      const top = rowIdx * this.rowHeight + this.barMargin;

      if (this.isDiamond(row)) {
        const size = Math.round(mainBarHeight / Math.SQRT2);
        return {
          left: (startX - size / 2) + 'px',
          width: size + 'px',
          top: (top + (mainBarHeight - size) / 2) + 'px',
          height: size + 'px',
          backgroundColor: row.eventColor
        };
      }

      const width = endX - startX;

      return {
        left: startX + 'px',
        width: Math.max(width, 1) + 'px',
        top: top + 'px',
        height: mainBarHeight + 'px',
        backgroundColor: row.eventColor
      };
    },

    isDiamond(row) {
      if (!row.startDate || !row.endDate) return false;
      return this.getDuration(row) <= 1;
    },

    isLabelOutside(row) {
      if (this.isDiamond(row)) return true;
      const startX = this.dateToX(row.startDate);
      const endX = this.dateToX(row.endDate);
      const width = endX - startX;
      // "0.00%" at 9px bold ≈ 30px; use that as threshold
      return width < 30;
    },

    getOutsideLabelStyle(row, rowIdx) {
      const mainBarHeight = this.hasVisibleBaselines ? 12 : 18;
      const top = rowIdx * this.rowHeight + this.barMargin;

      let leftPos;
      if (this.isDiamond(row)) {
        const startX = this.dateToX(row.startDate);
        leftPos = startX + mainBarHeight / 2 + 4;
      } else {
        leftPos = this.dateToX(row.endDate) + 4;
      }

      return {
        position: 'absolute',
        left: leftPos + 'px',
        top: top + 'px',
        height: mainBarHeight + 'px',
        lineHeight: mainBarHeight + 'px'
      };
    },

    getBaselineBarStyle(row, rowIdx, baseline, visibleIdx) {
      const startX = this.dateToX(baseline.startDate);
      const endX = this.dateToX(baseline.endDate);
      let width = endX - startX;
      if (width < 4) width = 4;

      const mainBarHeight = this.hasVisibleBaselines ? 12 : 18;
      const baselineHeight = 5;
      const baselineGap = 1;
      const top = rowIdx * this.rowHeight + this.barMargin + mainBarHeight + 2 + (visibleIdx * (baselineHeight + baselineGap));

      const colors = [
        this.colors.defaults.original.normal,  // baseline-0: Original planning
        this.colors.defaults.real.normal,      // baseline-1: Latest planning
        this.colors.defaults.inspected.normal  // baseline-2: Inspected progress
      ];

      return {
        left: startX + 'px',
        width: width + 'px',
        top: top + 'px',
        height: baselineHeight + 'px',
        backgroundColor: colors[baseline.index] || '#999'
      };
    },

    // Sticky year label - stays centered unless pushed by viewport edges
    getStickyLabelStyle(cell, isYearRow) {
      if (!isYearRow) return {};

      const scrollLeft = this.timelineScrollLeft;
      const viewportWidth = this.timelineViewportWidth || 800;
      const cellStart = cell.x;
      const cellEnd = cell.x + cell.width;
      const cellWidth = cell.width;
      const viewportEnd = scrollLeft + viewportWidth;
      const edgePadding = 18;

      // Cell completely outside viewport
      if (cellEnd <= scrollLeft || cellStart >= viewportEnd) {
        return { visibility: 'hidden' };
      }

      const idealPosition = cellWidth / 2;
      const idealAbsolute = cellStart + idealPosition;

      const minVisibleAbsolute = scrollLeft + edgePadding;
      const maxVisibleAbsolute = viewportEnd - edgePadding;

      let actualAbsolute = Math.max(minVisibleAbsolute, Math.min(maxVisibleAbsolute, idealAbsolute));

      const minCellAbsolute = cellStart + edgePadding;
      const maxCellAbsolute = cellEnd - edgePadding;
      actualAbsolute = Math.max(minCellAbsolute, Math.min(maxCellAbsolute, actualAbsolute));

      const labelPosition = actualAbsolute - cellStart;

      return {
        position: 'absolute',
        left: labelPosition + 'px',
        transform: 'translateX(-50%)',
        whiteSpace: 'nowrap'
      };
    },

    // =========================================================================
    // Dependencies rendering
    // =========================================================================

    getTaskBounds(task) {
      const idx = this.rowIndexMap[task.id];
      if (idx === undefined) return null;

      const mainBarHeight = this.hasVisibleBaselines ? 12 : 18;

      if (this.isDiamond(task)) {
        const cx = this.dateToX(task.startDate);
        const halfDiag = mainBarHeight / 2;
        const barTop = idx * this.rowHeight + this.barMargin;
        return {
          startX: cx - halfDiag,
          endX: cx + halfDiag,
          centerY: barTop + mainBarHeight / 2,
          barTop,
          barBottom: barTop + mainBarHeight,
          idx
        };
      }

      const startX = this.dateToX(task.startDate);
      const endX = this.dateToX(task.endDate);
      const barTop = idx * this.rowHeight + this.barMargin;
      const barBottom = barTop + mainBarHeight;
      const centerY = barTop + mainBarHeight / 2;

      return { startX, endX, centerY, barTop, barBottom, idx };
    },

    calculateSingleDependencyPath(dep, routeIndex = 0) {
      const fromTask = this.taskMap[dep.from];
      const toTask = this.taskMap[dep.to];
      const rowMap = this.rowIndexMap;

      if (!fromTask || !toTask || rowMap[fromTask.id] === undefined || rowMap[toTask.id] === undefined) return null;

      const from = this.getTaskBounds(fromTask);
      const to = this.getTaskBounds(toTask);

      if (!from || !to) return null;

      const path = this.createDependencyPath(from, to, dep.type, routeIndex);

      return {
        id: dep.id,
        path,
        from: dep.from,
        to: dep.to,
        type: dep.type,
        lag: dep.lag,
        lagUnit: dep.lagUnit
      };
    },

    calculateTreeDependencyPaths(deps) {
      const results = [];
      const firstDep = deps[0];
      const fromTask = this.taskMap[firstDep.from];
      const rowMap = this.rowIndexMap;

      if (!fromTask || rowMap[fromTask.id] === undefined) return results;

      const from = this.getTaskBounds(fromTask);
      if (!from) return results;

      const destinations = deps.map(dep => {
        const toTask = this.taskMap[dep.to];
        if (!toTask || rowMap[toTask.id] === undefined) return null;
        const to = this.getTaskBounds(toTask);
        if (!to) return null;
        return { dep, to };
      }).filter(d => d !== null).sort((a, b) => a.to.idx - b.to.idx);

      if (destinations.length === 0) return results;

      destinations.forEach((dest, i) => {
        const path = this.createDependencyPath(from, dest.to, dest.dep.type, i, destinations.length);

        results.push({
          id: dest.dep.id,
          path,
          from: dest.dep.from,
          to: dest.dep.to,
          type: dest.dep.type,
          lag: dest.dep.lag,
          lagUnit: dest.dep.lagUnit
        });
      });

      return results;
    },

    createDependencyPath(from, to, type, routeIndex = 0) {
      const r = 3; // corner radius
      const baseOffset = 10;
      const spacing = 6;
      const offset = baseOffset + routeIndex * spacing;

      let exitX, entryX, exitFromRight, enterFromLeft;

      switch (type) {
        case 0: // SS: Start → Start
          exitX = from.startX;
          entryX = to.startX;
          exitFromRight = false;
          enterFromLeft = true;
          break;
        case 1: // SF: Start → Finish
          exitX = from.startX;
          entryX = to.endX;
          exitFromRight = false;
          enterFromLeft = false;
          break;
        case 2: // FS: Finish → Start (most common)
          exitX = from.endX;
          entryX = to.startX;
          exitFromRight = true;
          enterFromLeft = true;
          break;
        case 3: // FF: Finish → Finish
          exitX = from.endX;
          entryX = to.endX;
          exitFromRight = true;
          enterFromLeft = false;
          break;
        default:
          exitX = from.endX;
          entryX = to.startX;
          exitFromRight = true;
          enterFromLeft = true;
      }

      const fromY = from.centerY;
      const toY = to.centerY;
      const isBelow = to.idx > from.idx;
      const sameRow = to.idx === from.idx;

      // Same row - simple horizontal line
      if (sameRow) {
        if ((exitFromRight && entryX > exitX) || (!exitFromRight && entryX < exitX)) {
          return `M ${exitX} ${fromY} L ${entryX} ${toY}`;
        }
        const loopY = fromY - this.rowHeight * 0.4;
        const leftX = Math.min(exitX, entryX) - offset;
        const rightX = Math.max(exitX, entryX) + offset;
        return `M ${exitX} ${fromY} L ${exitFromRight ? rightX : leftX} ${fromY} L ${exitFromRight ? rightX : leftX} ${loopY} L ${enterFromLeft ? leftX : rightX} ${loopY} L ${enterFromLeft ? leftX : rightX} ${toY} L ${entryX} ${toY}`;
      }

      // FS type (most common): Finish → Start
      if (type === 2) {
        if (entryX > exitX + offset) {
          const midX = exitX + offset;
          if (isBelow) {
            return `M ${exitX} ${fromY} L ${midX - r} ${fromY} Q ${midX} ${fromY} ${midX} ${fromY + r} L ${midX} ${toY - r} Q ${midX} ${toY} ${midX + r} ${toY} L ${entryX} ${toY}`;
          } else {
            return `M ${exitX} ${fromY} L ${midX - r} ${fromY} Q ${midX} ${fromY} ${midX} ${fromY - r} L ${midX} ${toY + r} Q ${midX} ${toY} ${midX + r} ${toY} L ${entryX} ${toY}`;
          }
        } else {
          const routeRightX = exitX + offset;
          const routeLeftX = entryX - offset;
          const midY = isBelow
            ? from.barBottom + 4 + routeIndex * spacing
            : from.barTop - 4 - routeIndex * spacing;

          if (isBelow) {
            return `M ${exitX} ${fromY} L ${routeRightX - r} ${fromY} Q ${routeRightX} ${fromY} ${routeRightX} ${fromY + r} L ${routeRightX} ${midY - r} Q ${routeRightX} ${midY} ${routeRightX - r} ${midY} L ${routeLeftX + r} ${midY} Q ${routeLeftX} ${midY} ${routeLeftX} ${midY + r} L ${routeLeftX} ${toY - r} Q ${routeLeftX} ${toY} ${routeLeftX + r} ${toY} L ${entryX} ${toY}`;
          } else {
            return `M ${exitX} ${fromY} L ${routeRightX - r} ${fromY} Q ${routeRightX} ${fromY} ${routeRightX} ${fromY - r} L ${routeRightX} ${midY + r} Q ${routeRightX} ${midY} ${routeRightX - r} ${midY} L ${routeLeftX + r} ${midY} Q ${routeLeftX} ${midY} ${routeLeftX} ${midY - r} L ${routeLeftX} ${toY + r} Q ${routeLeftX} ${toY} ${routeLeftX + r} ${toY} L ${entryX} ${toY}`;
          }
        }
      }

      // SS type: Start → Start
      if (type === 0) {
        const routeX = Math.min(exitX, entryX) - offset;
        if (isBelow) {
          return `M ${exitX} ${fromY} L ${routeX + r} ${fromY} Q ${routeX} ${fromY} ${routeX} ${fromY + r} L ${routeX} ${toY - r} Q ${routeX} ${toY} ${routeX + r} ${toY} L ${entryX} ${toY}`;
        } else {
          return `M ${exitX} ${fromY} L ${routeX + r} ${fromY} Q ${routeX} ${fromY} ${routeX} ${fromY - r} L ${routeX} ${toY + r} Q ${routeX} ${toY} ${routeX + r} ${toY} L ${entryX} ${toY}`;
        }
      }

      // FF type: Finish → Finish
      if (type === 3) {
        const routeX = Math.max(exitX, entryX) + offset;
        if (isBelow) {
          return `M ${exitX} ${fromY} L ${routeX - r} ${fromY} Q ${routeX} ${fromY} ${routeX} ${fromY + r} L ${routeX} ${toY - r} Q ${routeX} ${toY} ${routeX - r} ${toY} L ${entryX} ${toY}`;
        } else {
          return `M ${exitX} ${fromY} L ${routeX - r} ${fromY} Q ${routeX} ${fromY} ${routeX} ${fromY - r} L ${routeX} ${toY + r} Q ${routeX} ${toY} ${routeX - r} ${toY} L ${entryX} ${toY}`;
        }
      }

      // SF type: Start → Finish
      if (type === 1) {
        const leftX = exitX - offset;
        const rightX = entryX + offset;
        const midY = (fromY + toY) / 2;

        if (isBelow) {
          return `M ${exitX} ${fromY} L ${leftX + r} ${fromY} Q ${leftX} ${fromY} ${leftX} ${fromY + r} L ${leftX} ${midY} L ${rightX} ${midY} L ${rightX} ${toY - r} Q ${rightX} ${toY} ${rightX - r} ${toY} L ${entryX} ${toY}`;
        } else {
          return `M ${exitX} ${fromY} L ${leftX + r} ${fromY} Q ${leftX} ${fromY} ${leftX} ${fromY - r} L ${leftX} ${midY} L ${rightX} ${midY} L ${rightX} ${toY + r} Q ${rightX} ${toY} ${rightX - r} ${toY} L ${entryX} ${toY}`;
        }
      }

      // Fallback
      return `M ${exitX} ${fromY} L ${entryX} ${toY}`;
    },

    // =========================================================================
    // Zoom
    // =========================================================================

    zoomIn() {
      if (this.zoomLevel >= this.zoomLevels.length - 1) return;
      this.zoomWithCenter(1);
    },

    zoomOut() {
      if (this.zoomLevel <= 0) return;
      this.zoomWithCenter(-1);
    },

    zoomWithCenter(direction) {
      const rightBody = this.$refs.rightBody;
      if (!rightBody) return;

      const viewportWidth = rightBody.clientWidth;
      const scrollLeftBefore = rightBody.scrollLeft;
      const centerX = scrollLeftBefore + viewportWidth / 2;
      const oldDayWidth = this.currentZoom.dayWidth;
      const newZoomLevel = this.zoomLevel + direction;
      const newDayWidth = this.zoomLevels[newZoomLevel].dayWidth;

      const scale = newDayWidth / oldDayWidth;
      const newCenterX = centerX * scale;
      const newScrollLeft = Math.max(0, newCenterX - viewportWidth / 2);

      this.timelineScrollLeft = newScrollLeft;
      this.timelineViewportWidth = viewportWidth;
      this.zoomLevel = newZoomLevel;

      this.$nextTick(() => {
        rightBody.scrollLeft = newScrollLeft;
      });
    },

    zoomToFit() {
      if (!this.$refs.rightPanel || !this.projectStartDate || !this.projectEndDate) return;

      const containerWidth = this.$refs.rightPanel.clientWidth;
      const totalDays = moment(this.projectEndDate).diff(moment(this.projectStartDate), 'days') + 1;

      if (totalDays <= 0) return;

      const padding = 40;
      const targetDayWidth = (containerWidth - padding) / totalDays;

      let bestLevel = 0;
      for (let i = 0; i < this.zoomLevels.length; i++) {
        if (this.zoomLevels[i].dayWidth <= targetDayWidth) {
          bestLevel = i;
        }
      }
      this.zoomLevel = bestLevel;

      this.$nextTick(() => {
        if (this.$refs.rightBody && this.projectStartDate) {
          const projectStartX = this.dateToX(this.projectStartDate);
          const projectEndX = this.dateToX(this.projectEndDate);
          const contentWidth = projectEndX - projectStartX;
          const viewportWidth = this.$refs.rightBody.clientWidth;

          const scrollLeft = projectStartX - (viewportWidth - contentWidth) / 2;
          this.$refs.rightBody.scrollLeft = Math.max(0, scrollLeft);

          this.timelineScrollLeft = this.$refs.rightBody.scrollLeft;
          this.timelineViewportWidth = viewportWidth;
        }
      });
    },

    handleWheel(event) {
      if (event.ctrlKey) {
        event.preventDefault();

        const rightBody = this.$refs.rightBody;
        if (!rightBody) return;

        if (!this._pendingZoom) {
          this._pendingZoom = { deltaY: 0, clientX: event.clientX, clientY: event.clientY };
        }
        this._pendingZoom.deltaY += event.deltaY;
        this._pendingZoom.clientX = event.clientX;
        this._pendingZoom.clientY = event.clientY;

        if (!this._zoomRafId) {
          this._zoomRafId = requestAnimationFrame(() => {
            this._zoomRafId = null;
            const zoom = this._pendingZoom;
            this._pendingZoom = null;
            if (!zoom) return;

            const rect = rightBody.getBoundingClientRect();
            const mouseX = zoom.clientX - rect.left;
            const scrollLeftBefore = rightBody.scrollLeft;
            const positionInTimeline = scrollLeftBefore + mouseX;
            const oldDayWidth = this.currentZoom.dayWidth;

            let newZoomLevel = this.zoomLevel;
            if (zoom.deltaY < 0) {
              if (newZoomLevel >= this.zoomLevels.length - 1) return;
              newZoomLevel++;
            } else {
              if (newZoomLevel <= 0) return;
              newZoomLevel--;
            }

            const newDayWidth = this.zoomLevels[newZoomLevel].dayWidth;
            const scale = newDayWidth / oldDayWidth;
            const newScrollLeft = Math.max(0, positionInTimeline * scale - mouseX);

            this.timelineScrollLeft = newScrollLeft;
            this.timelineViewportWidth = rightBody.clientWidth;
            this.zoomLevel = newZoomLevel;

            this.$nextTick(() => {
              rightBody.scrollLeft = newScrollLeft;
            });
          });
        }
      }
    },

    // =========================================================================
    // Scroll sync
    // =========================================================================

    syncScroll(source) {
      if (this._isSyncing) return;
      this._isSyncing = true;

      if (source === 'left') {
        if (this.$refs.rightBody) {
          this.$refs.rightBody.scrollTop = this.$refs.leftBody.scrollTop;
        }
        if (this.$refs.tableHeader) {
          this.$refs.tableHeader.style.transform = 'translateX(-' + this.$refs.leftBody.scrollLeft + 'px)';
        }
      } else {
        if (this.$refs.leftBody) {
          this.$refs.leftBody.scrollTop = this.$refs.rightBody.scrollTop;
        }
        if (this.$refs.timeHeaderWrapper) {
          this.$refs.timeHeaderWrapper.scrollLeft = this.$refs.rightBody.scrollLeft;
        }
      }

      this._isSyncing = false;

      if (!this._rafSyncId) {
        this._rafSyncId = requestAnimationFrame(() => {
          this._rafSyncId = null;
          if (this.$refs.rightBody) {
            this.timelineScrollLeft = this.$refs.rightBody.scrollLeft;
            this.timelineViewportWidth = this.$refs.rightBody.clientWidth;
          }
          const body = this.$refs.leftBody || this.$refs.rightBody;
          if (body) {
            this.visibleScrollTop = body.scrollTop;
          }
          this.scheduleDependencyPathsUpdate();
        });
      }
    },

    // =========================================================================
    // Panning - Right Panel (Timeline)
    // =========================================================================

    // Detecta si el mousedown cayo sobre la scrollbar nativa del contenedor.
    // En ese caso no iniciamos pan: dejamos que el navegador haga scroll y que el
    // evento 'scroll' sincronice los headers en vivo (igual que con la rueda). Asi
    // evitamos que _isSyncing bloquee syncScroll mientras se arrastra la scrollbar.
    isScrollbarMousedown(event, el) {
      if (!el || event.target !== el) return false;
      return event.offsetX >= el.clientWidth || event.offsetY >= el.clientHeight;
    },

    startPan(event) {
      if (event.button !== 0) return;
      if (this.isScrollbarMousedown(event, this.$refs.rightBody)) return;
      // Cancelamos cualquier dependency path update pendiente para no bloquear durante el pan
      if (this._depPathsTimer) { clearTimeout(this._depPathsTimer); this._depPathsTimer = null; }
      this._isPanningActive = false;
      // No bloqueamos syncScroll todavia: solo cuando el pan se active de verdad (en handlePan).
      // Asi, si esto resulta ser un arrastre de scrollbar, el scroll nativo se sincroniza en vivo.
      this._panStartX = event.clientX;
      this._panStartY = event.clientY;
      this._panStartScrollLeft = this.$refs.rightBody.scrollLeft;
      this._panStartScrollTop = this.$refs.rightBody.scrollTop;
      document.addEventListener('mousemove', this._boundHandlePan, { passive: true });
      document.addEventListener('mouseup', this._boundEndPan);
    },

    handlePan(event) {
      const clientX = event.clientX;
      const clientY = event.clientY;

      if (!this._isPanningActive) {
        // Arrastre de scrollbar nativa (incluye scrollbars overlay que no reservan espacio
        // de layout y que el test geometrico no puede detectar): si el scroll del contenedor
        // ya cambio por si solo, abortamos el pan y dejamos que el evento 'scroll' sincronice
        // los headers en vivo. Robusto en cualquier navegador/SO.
        const rb = this.$refs.rightBody;
        if (rb && (rb.scrollLeft !== this._panStartScrollLeft || rb.scrollTop !== this._panStartScrollTop)) {
          this._isSyncing = false;
          document.removeEventListener('mousemove', this._boundHandlePan);
          document.removeEventListener('mouseup', this._boundEndPan);
          return;
        }
        const dx = clientX - this._panStartX;
        const dy = clientY - this._panStartY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          this._isPanningActive = true;
          this._isSyncing = true;
          // Aplicamos estilos de pan directamente en los containers (evita recalc de estilos global)
          this.$refs.rightBody.style.cursor = 'grabbing';
          this.$refs.rightBody.style.userSelect = 'none';
          if (this.$refs.leftBody) {
            this.$refs.leftBody.style.cursor = 'grabbing';
            this.$refs.leftBody.style.userSelect = 'none';
          }
        } else {
          return;
        }
      }

      if (this._rafPanId) cancelAnimationFrame(this._rafPanId);
      this._rafPanId = requestAnimationFrame(() => {
        this._rafPanId = null;
        if (!this._isPanningActive) return;

        const dx = clientX - this._panStartX;
        const dy = clientY - this._panStartY;

        this.$refs.rightBody.scrollLeft = this._panStartScrollLeft - dx;
        this.$refs.rightBody.scrollTop = this._panStartScrollTop - dy;
        if (this.$refs.leftBody) {
          this.$refs.leftBody.scrollTop = this.$refs.rightBody.scrollTop;
        }
        if (this.$refs.timeHeaderWrapper) {
          this.$refs.timeHeaderWrapper.scrollLeft = this.$refs.rightBody.scrollLeft;
        }
        if (this.$refs.tableHeader && this.$refs.leftBody) {
          this.$refs.tableHeader.style.transform = 'translateX(-' + this.$refs.leftBody.scrollLeft + 'px)';
        }
      });
    },

    endPan() {
      this._isPanningActive = false;
      this._isSyncing = false;
      if (this._rafPanId) {
        cancelAnimationFrame(this._rafPanId);
        this._rafPanId = null;
      }
      if (this.$refs.rightBody) {
        this.$refs.rightBody.style.cursor = '';
        this.$refs.rightBody.style.userSelect = '';
      }
      if (this.$refs.leftBody) {
        this.$refs.leftBody.style.cursor = '';
        this.$refs.leftBody.style.userSelect = '';
      }
      document.removeEventListener('mousemove', this._boundHandlePan);
      document.removeEventListener('mouseup', this._boundEndPan);

      requestAnimationFrame(() => {
        if (this.$refs.rightBody) {
          this.timelineScrollLeft = this.$refs.rightBody.scrollLeft;
          this.timelineViewportWidth = this.$refs.rightBody.clientWidth;
          this.visibleScrollTop = this.$refs.rightBody.scrollTop;
        }
        this.scheduleDependencyPathsUpdate();
      });
    },

    // =========================================================================
    // Panning - Left Panel (Table)
    // =========================================================================

    startLeftPan(event) {
      if (event.button !== 0) return;
      if (this.isScrollbarMousedown(event, this.$refs.leftBody)) return;
      if (event.target.closest('.expand-icon') || event.target.closest('.dependency-badge')) {
        return;
      }
      if (this._depPathsTimer) { clearTimeout(this._depPathsTimer); this._depPathsTimer = null; }
      this._isLeftPanningActive = false;
      // Igual que en startPan: no bloqueamos syncScroll hasta que el pan se active de verdad.
      this._leftPanStartX = event.clientX;
      this._leftPanStartY = event.clientY;
      this._leftPanStartScrollLeft = this.$refs.leftBody.scrollLeft;
      this._leftPanStartScrollTop = this.$refs.leftBody.scrollTop;
      document.addEventListener('mousemove', this._boundHandleLeftPan, { passive: true });
      document.addEventListener('mouseup', this._boundEndLeftPan);
    },

    handleLeftPan(event) {
      const clientX = event.clientX;
      const clientY = event.clientY;

      if (!this._isLeftPanningActive) {
        // Mismo criterio que en handlePan: si el scroll del contenedor ya cambio solo,
        // es un arrastre de scrollbar (incl. overlay) y no debemos panear.
        const lb = this.$refs.leftBody;
        if (lb && (lb.scrollLeft !== this._leftPanStartScrollLeft || lb.scrollTop !== this._leftPanStartScrollTop)) {
          this._isSyncing = false;
          document.removeEventListener('mousemove', this._boundHandleLeftPan);
          document.removeEventListener('mouseup', this._boundEndLeftPan);
          return;
        }
        const dx = clientX - this._leftPanStartX;
        const dy = clientY - this._leftPanStartY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          this._isLeftPanningActive = true;
          this._isSyncing = true;
          this.$refs.leftBody.style.cursor = 'grabbing';
          this.$refs.leftBody.style.userSelect = 'none';
          if (this.$refs.rightBody) {
            this.$refs.rightBody.style.cursor = 'grabbing';
            this.$refs.rightBody.style.userSelect = 'none';
          }
        } else {
          return;
        }
      }

      if (this._rafLeftPanId) cancelAnimationFrame(this._rafLeftPanId);
      this._rafLeftPanId = requestAnimationFrame(() => {
        this._rafLeftPanId = null;
        if (!this._isLeftPanningActive) return;

        const dx = clientX - this._leftPanStartX;
        const dy = clientY - this._leftPanStartY;

        this.$refs.leftBody.scrollLeft = this._leftPanStartScrollLeft - dx;
        this.$refs.leftBody.scrollTop = this._leftPanStartScrollTop - dy;
        if (this.$refs.rightBody) {
          this.$refs.rightBody.scrollTop = this.$refs.leftBody.scrollTop;
        }
        if (this.$refs.tableHeader) {
          this.$refs.tableHeader.style.transform = 'translateX(-' + this.$refs.leftBody.scrollLeft + 'px)';
        }
        if (this.$refs.timeHeaderWrapper && this.$refs.rightBody) {
          this.$refs.timeHeaderWrapper.scrollLeft = this.$refs.rightBody.scrollLeft;
        }
      });
    },

    endLeftPan() {
      this._isLeftPanningActive = false;
      this._isSyncing = false;
      if (this._rafLeftPanId) {
        cancelAnimationFrame(this._rafLeftPanId);
        this._rafLeftPanId = null;
      }
      if (this.$refs.leftBody) {
        this.$refs.leftBody.style.cursor = '';
        this.$refs.leftBody.style.userSelect = '';
      }
      if (this.$refs.rightBody) {
        this.$refs.rightBody.style.cursor = '';
        this.$refs.rightBody.style.userSelect = '';
      }
      document.removeEventListener('mousemove', this._boundHandleLeftPan);
      document.removeEventListener('mouseup', this._boundEndLeftPan);

      requestAnimationFrame(() => {
        if (this.$refs.leftBody) {
          this.visibleScrollTop = this.$refs.leftBody.scrollTop;
        }
        if (this.$refs.rightBody) {
          this.timelineScrollLeft = this.$refs.rightBody.scrollLeft;
          this.timelineViewportWidth = this.$refs.rightBody.clientWidth;
        }
        this.scheduleDependencyPathsUpdate();
      });
    },

    // =========================================================================
    // Splitter
    // =========================================================================

    startSplitterDrag(event) {
      event.preventDefault();
      this.splitterDragging = true;
      this.splitterStartX = event.clientX;
      this.splitterStartWidth = this.leftPanelWidth;
      this.splitterScrollLeft = this.$refs.leftBody ? this.$refs.leftBody.scrollLeft : 0;
      this.splitterScrollTop = this.$refs.leftBody ? this.$refs.leftBody.scrollTop : 0;
      document.body.classList.add('splitter-dragging');
      document.addEventListener('mousemove', this.handleSplitterDrag);
      document.addEventListener('mouseup', this.endSplitterDrag);
    },

    handleSplitterDrag(event) {
      if (!this.splitterDragging) return;
      event.preventDefault();
      const diff = event.clientX - this.splitterStartX;
      const newWidth = this.splitterStartWidth + diff;
      if (newWidth >= 100 && newWidth <= window.innerWidth - 200) {
        this.leftPanelWidth = newWidth;
      }
      if (this.$refs.leftBody) {
        this.$refs.leftBody.scrollLeft = this.splitterScrollLeft;
        this.$refs.leftBody.scrollTop = this.splitterScrollTop;
      }
    },

    endSplitterDrag() {
      this.splitterDragging = false;
      document.body.classList.remove('splitter-dragging');
      document.removeEventListener('mousemove', this.handleSplitterDrag);
      document.removeEventListener('mouseup', this.endSplitterDrag);
    },

    // =========================================================================
    // Column resize
    // =========================================================================

    startColumnResize(event, columnIndex) {
      event.preventDefault();
      const visibleIdx = columnIndex;
      const actualIdx = this.columns.findIndex(c => c.field === this.visibleColumns[visibleIdx].field);

      this._colResizeActive = true;
      this._colResizeIndex = actualIdx;
      this._colResizeStartX = event.clientX;
      this._colResizeStartWidth = this.columns[actualIdx].width;

      document.body.classList.add('column-resizing');
      document.addEventListener('mousemove', this._boundHandleColumnResize);
      document.addEventListener('mouseup', this._boundEndColumnResize);
    },

    handleColumnResize(event) {
      if (!this._colResizeActive) return;

      const clientX = event.clientX;

      if (this._colResizeRafId) {
        cancelAnimationFrame(this._colResizeRafId);
      }

      this._colResizeRafId = requestAnimationFrame(() => {
        const diff = clientX - this._colResizeStartX;
        const newWidth = this._colResizeStartWidth + diff;
        const column = this.columns[this._colResizeIndex];

        if (column && newWidth >= column.minWidth) {
          column.width = newWidth;
        }
      });
    },

    endColumnResize() {
      this._colResizeActive = false;

      if (this._colResizeRafId) {
        cancelAnimationFrame(this._colResizeRafId);
        this._colResizeRafId = null;
      }

      document.body.classList.remove('column-resizing');
      document.removeEventListener('mousemove', this._boundHandleColumnResize);
      document.removeEventListener('mouseup', this._boundEndColumnResize);
    },

    // =========================================================================
    // Column drag reordering
    // =========================================================================

    startColumnDrag(event, columnIndex) {
      if (event.target.classList.contains('column-resize-handle')) return;

      const headerCell = event.currentTarget;
      const rect = headerCell.getBoundingClientRect();
      const col = this.visibleColumns[columnIndex];

      this.columnDragging = {
        active: true,
        sourceIndex: columnIndex,
        targetIndex: columnIndex,
        startX: event.clientX,
        currentX: event.clientX,
        startOffsetX: event.clientX - rect.left,
        headerTop: rect.top,
        columnWidth: col.width,
        hasMoved: false,
        justFinishedDrag: false
      };

      document.addEventListener('mousemove', this.handleColumnDrag);
      document.addEventListener('mouseup', this.endColumnDrag);
    },

    handleColumnDrag(event) {
      if (!this.columnDragging.active) return;

      const moveDistance = Math.abs(event.clientX - this.columnDragging.startX);
      if (moveDistance > 5) {
        if (!this.columnDragging.hasMoved) {
          this.columnDragging.hasMoved = true;
          document.body.classList.add('column-dragging-active');
        }

        this.columnDragging.currentX = event.clientX;
        this.updateDragTargetIndex(event.clientX);
      }
    },

    updateDragTargetIndex(mouseX) {
      if (!this.$refs.headerRow) return;

      const headerCells = this.$refs.headerRow.querySelectorAll('.gantt-table-header-cell');
      const sourceIndex = this.columnDragging.sourceIndex;
      let newTargetIndex = sourceIndex;

      for (let i = 0; i < headerCells.length; i++) {
        const cell = headerCells[i];
        const rect = cell.getBoundingClientRect();
        const cellCenter = rect.left + rect.width / 2;

        if (mouseX < cellCenter) {
          newTargetIndex = i;
          break;
        }
        newTargetIndex = i;
      }

      if (mouseX > headerCells[headerCells.length - 1].getBoundingClientRect().right - 20) {
        newTargetIndex = headerCells.length - 1;
      }

      if (this.columnDragging.targetIndex !== newTargetIndex) {
        this.columnDragging.targetIndex = newTargetIndex;
      }
    },

    getEffectiveColumnWidth(col) {
      if (col.field === 'successors' || col.field === 'predecessors') {
        return Math.max(col.width, this.dependencyColumnWidths[col.field]);
      }
      return col.width;
    },

    getColumnStyle(col, idx) {
      var w = this.getEffectiveColumnWidth(col);
      const baseStyle = {
        width: w + 'px',
        minWidth: col.minWidth + 'px'
      };

      if (!this.columnDragging.active || !this.columnDragging.hasMoved) {
        return baseStyle;
      }

      const sourceIdx = this.columnDragging.sourceIndex;
      const targetIdx = this.columnDragging.targetIndex;
      const sourceWidth = this.columnDragging.columnWidth;

      if (idx === sourceIdx) {
        return {
          width: '0px',
          minWidth: '0px',
          padding: '0',
          overflow: 'hidden',
          borderWidth: '0',
          opacity: '0',
          transition: 'all 0.15s ease'
        };
      }

      if (sourceIdx !== targetIdx) {
        if (sourceIdx < targetIdx && idx === targetIdx) {
          return {
            ...baseStyle,
            marginRight: sourceWidth + 'px',
            transition: 'margin 0.15s ease'
          };
        } else if (sourceIdx > targetIdx && idx === targetIdx) {
          return {
            ...baseStyle,
            marginLeft: sourceWidth + 'px',
            transition: 'margin 0.15s ease'
          };
        }
      }

      return {
        ...baseStyle,
        transition: 'margin 0.15s ease'
      };
    },

    endColumnDrag(event) {
      const wasDragging = this.columnDragging.hasMoved;

      if (this.columnDragging.active && wasDragging) {
        const { sourceIndex, targetIndex } = this.columnDragging;

        if (sourceIndex !== targetIndex && targetIndex !== null) {
          const sourceCol = this.visibleColumns[sourceIndex];
          const targetCol = this.visibleColumns[targetIndex];

          const sourceActualIndex = this.columns.findIndex(c => c.field === sourceCol.field);
          const targetActualIndex = this.columns.findIndex(c => c.field === targetCol.field);

          const [movedColumn] = this.columns.splice(sourceActualIndex, 1);
          const newTargetIndex = this.columns.findIndex(c => c.field === targetCol.field);

          if (sourceActualIndex < targetActualIndex) {
            this.columns.splice(newTargetIndex + 1, 0, movedColumn);
          } else {
            this.columns.splice(newTargetIndex, 0, movedColumn);
          }
        }
      }

      this.columnDragging = {
        active: false,
        sourceIndex: null,
        targetIndex: null,
        startX: 0,
        currentX: 0,
        startOffsetX: 0,
        headerTop: 0,
        columnWidth: 0,
        hasMoved: false,
        justFinishedDrag: wasDragging
      };

      if (wasDragging) {
        setTimeout(() => {
          this.columnDragging.justFinishedDrag = false;
        }, 100);
      }

      document.body.classList.remove('column-dragging-active');
      document.removeEventListener('mousemove', this.handleColumnDrag);
      document.removeEventListener('mouseup', this.endColumnDrag);
    },

    handleColumnClick(event, field) {
      if (!this.columnDragging.justFinishedDrag) {
        this.toggleSort(field);
      }
    },

    // =========================================================================
    // Sorting
    // =========================================================================

    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    sortNodesRecursive(nodes, result) {
      if (!nodes || !nodes.length) return;

      const sortedNodes = [...nodes].sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (this.sortField === 'code') {
          // 3.1, 3.2, … 3.9, 3.10 — en vez de 3.1, 3.10, 3.2
          const comparison = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
          return this.sortDirection === 'asc' ? comparison : -comparison;
        }

        if (this.sortField.includes('date') || this.sortField.includes('Date')) {
          aVal = aVal ? moment(aVal).valueOf() : 0;
          bVal = bVal ? moment(bVal).valueOf() : 0;
        } else if (typeof aVal === 'number' || typeof bVal === 'number') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }

        let comparison = 0;
        if (aVal < bVal) comparison = -1;
        else if (aVal > bVal) comparison = 1;

        return this.sortDirection === 'asc' ? comparison : -comparison;
      });

      for (const node of sortedNodes) {
        result.push(node);
        if (node.children && node.children.length) {
          this.sortNodesRecursive(node.children, result);
        }
      }
    },

    // =========================================================================
    // Column context menu
    // =========================================================================

    showColumnContextMenu(event, columnIndex) {
      this.columnContextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        columnIndex
      };
    },

    toggleColumnVisibility(col) {
      col.visible = !col.visible;
    },

    // =========================================================================
    // Tooltips
    // =========================================================================

    showTaskTooltip(event, row) {
      // Copiamos solo los campos necesarios — NO asignamos el row completo
      // porque Vue 2 haría deep observation del objeto (incluyendo children con miles de descendientes)
      this.tooltip = {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY + 10,
        type: 'task',
        data: {
          name: row.name,
          percentDone: row.percentDone,
          startDate: row.startDate,
          endDate: row.endDate,
          gantt_real_start_date: row.gantt_real_start_date,
          gantt_real_end_date: row.gantt_real_end_date,
          totalFloat: row.totalFloat,
          baselines: row.baselines,
        }
      };
    },

    showBaselineTooltip(event, row, baseline) {
      this.tooltip = {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY + 10,
        type: 'baseline',
        data: { row: { name: row.name }, baseline }
      };
    },

    showDependencyTooltip(event, dep) {
      this.tooltip = {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY + 10,
        type: 'dependency',
        data: dep
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    // =========================================================================
    // Formatting / Helpers
    // =========================================================================

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    },

    calculateDuration(row) {
      if (!row.startDate || !row.endDate) return '';
      const days = moment(row.endDate).diff(moment(row.startDate), 'days') + 1;
      return days + ' ' + this.$t('days');
    },

    getStatusColor(row) {
      if (row.type === 'assignment' && this.colors.assignment[row.status]) {
        return this.colors.assignment[row.status].normal;
      }
      if (row.type === 'activity' && this.colors.activity[row.status]) {
        return this.colors.activity[row.status].normal;
      }
      return '#999';
    },

    getStatusLabel(row) {
      if (row.type === 'assignment' && this.labels.assignment[row.status]) {
        return this.$t(this.labels.assignment[row.status]);
      }
      if (row.type === 'activity' && this.labels.activity[row.status]) {
        return this.$t(this.labels.activity[row.status]);
      }
      return '';
    },

    getSuccessors(taskId) {
      return this.successorMap[taskId] || [];
    },

    getPredecessors(taskId) {
      return this.predecessorMap[taskId] || [];
    },

    getTaskCode(taskId) {
      const task = this.taskMap[taskId];
      return task ? task.code : taskId;
    },

    getTaskName(taskId) {
      const task = this.taskMap[taskId];
      return task ? task.name : taskId;
    },

    isCenteredColumn(col) {
      return col.field === 'status' || col.field === 'percentDone' || col.field === 'latestPlanningProgress' || col.field === 'duration' || col.field === 'weight' || col.field.includes('date') || col.field.includes('Date');
    },

    getLatestPlanningProgress(row) {
      if (row.latestPlanningProgress !== undefined && row.latestPlanningProgress !== null) {
        return row.latestPlanningProgress;
      }
      return row.baselines?.[1]?.progress ?? 0;
    },

    formatWeight(row) {
      if (row.type === 'activity' && row.children && row.children.length) {
        return '';
      }
      if (row.weight === null || row.weight === undefined || row.weight === '') {
        return '';
      }
      return `${row.weight} %`;
    },

    getCellTitle(row, col) {
      if (col.field === 'status') return '';
      if (col.field === 'percentDone' || col.field === 'latestPlanningProgress') return '';
      if (col.field === 'weight') return this.formatWeight(row);
      if (col.field === 'successors' || col.field === 'predecessors') return '';
      if (col.field.includes('date') || col.field.includes('Date')) {
        return this.formatDate(row[col.field]);
      }
      return row[col.field] || '';
    },

    // =========================================================================
    // Navigation / Selection
    // =========================================================================

    scrollToTask(task) {
      const rowIdx = this.sortedVisibleRows.findIndex(r => r.id === task.id);
      if (rowIdx === -1) return;

      // Vertical: scroll nativo + sync reactive state
      const targetTop = rowIdx * this.rowHeight - this.viewportHeight / 2 + this.rowHeight / 2;
      if (this.$refs.leftBody) {
        this.$refs.leftBody.scrollTop = Math.max(0, targetTop);
      }
      if (this.$refs.rightBody) {
        this.$refs.rightBody.scrollTop = Math.max(0, targetTop);
      }
      this.visibleScrollTop = Math.max(0, targetTop);

      if (this.$refs.rightBody && task.startDate) {
        const taskX = this.dateToX(task.startDate);
        const taskEndX = this.dateToX(task.endDate);
        const taskWidth = taskEndX - taskX;
        const containerWidth = this.$refs.rightBody.clientWidth;

        const targetLeft = taskX - (containerWidth / 2) + (taskWidth / 2);
        this.$refs.rightBody.scrollLeft = Math.max(0, targetLeft);
      }
    },

    selectRow(row) {
      this.selectedRowId = row.id;
      this.$nextTick(() => {
        this.scrollToTask(row);
      });
    },

    // =========================================================================
    // Menus
    // =========================================================================

    toggleSettingsMenu() {
      this.showSettingsMenu = !this.showSettingsMenu;
      this.showViewsMenu = false;
    },

    toggleViewsMenu(event) {
      if (!this.showViewsMenu) {
        const btn = event.currentTarget;
        const rect = btn.getBoundingClientRect();
        this.viewsMenuPosition = {
          x: rect.left,
          y: rect.bottom + 4
        };
      }
      this.showViewsMenu = !this.showViewsMenu;
      this.showSettingsMenu = false;
    },

    handleGlobalClick(event) {
      if (!event.target.closest('.toolbar-dropdown')) {
        this.showSettingsMenu = false;
        this.showViewsMenu = false;
      }
      if (!event.target.closest('.critical-path-dropdown')) {
        this.showCriticalMenu = false;
      }
      if (!event.target.closest('.column-context-menu') && !event.target.closest('.gantt-table-header-cell')) {
        this.columnContextMenu.visible = false;
      }
    },

    // =========================================================================
    // Screenshot
    // =========================================================================

    async captureScreenshot() {
      if (this.isCapturing) return;
      this.isCapturing = true;
      this.tooltip.visible = false;

      // Temporarily clear selected row so it doesn't appear highlighted in screenshot
      const savedSelectedRowId = this.selectedRowId;
      this.selectedRowId = null;

      await this.$nextTick();
      // Wait for Vue to finish rendering all rows (virtual scroll is disabled via isCapturing)
      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        const html2canvas = (await import('html2canvas')).default;

        const container = this.$refs.ganttContainer;
        const rh = this.rowHeight;
        const rowsHeight = this.totalRowsHeight;
        const headerHeight = this.timeHeaderHeight;
        let fullHeight = rowsHeight + headerHeight + rh;

        // === Calculate capture dimensions ===
        var totalColumnsW = this.totalColumnsWidth;

        // Right panel: find actual bar range (earliest start to latest end)
        var minBarX = Infinity, maxBarX = -Infinity;
        var self = this;
        this.sortedVisibleRows.forEach(function(row) {
          if (row.startDate && row.endDate) {
            var sx = self.dateToX(row.startDate);
            var ex = self.dateToX(row.endDate);
            if (self.isDiamond(row)) {
              var ds = self.hasVisibleBaselines ? 12 : 18;
              if (sx - ds / 2 < minBarX) minBarX = sx - ds / 2;
              if (sx + ds / 2 > maxBarX) maxBarX = sx + ds / 2;
            } else {
              if (sx < minBarX) minBarX = sx;
              if (ex > maxBarX) maxBarX = ex;
            }
          }
          if (row.baselines) {
            row.baselines.forEach(function(bl) {
              if (bl.startDate && bl.endDate) {
                var bsx = self.dateToX(bl.startDate);
                var bex = self.dateToX(bl.endDate);
                if (bsx < minBarX) minBarX = bsx;
                if (bex > maxBarX) maxBarX = bex;
              }
            });
          }
        });
        if (minBarX === Infinity) { minBarX = 0; maxBarX = 1000; }

        var barPadding = 80;
        var captureStartX = Math.max(0, minBarX - barPadding);
        var captureEndX = maxBarX + barPadding;
        var timelineW = captureEndX - captureStartX;

        var splitterW = 6;
        var totalW = totalColumnsW + splitterW + timelineW;

        // === Clone off-screen ===
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'position:fixed;left:-99999px;top:0;pointer-events:none;overflow:visible;';
        document.body.appendChild(wrapper);

        const clone = container.cloneNode(true);
        wrapper.appendChild(clone);

        // Remove UI elements that shouldn't appear in the capture
        ['.screenshot-overlay', '.gantt-toolbar', '.gantt-tooltip', '.gantt-search-bar',
         '.column-context-menu', '.column-drag-ghost', '.floating-now-label'
        ].forEach(function(sel) {
          var el = clone.querySelector(sel);
          if (el) el.remove();
        });
        clone.querySelectorAll('.column-drag-handle, .column-resize-handle').forEach(function(el) {
          el.remove();
        });
        clone.querySelectorAll('.sort-icon').forEach(function(el) {
          el.style.display = 'none';
        });

        // === Exclude dependency columns (predecessors/successors) from capture ===
        var excludedFields = { successors: true, predecessors: true };
        var visibleCols = self.visibleColumns;
        var excludedIndices = {};
        for (var ei = 0; ei < visibleCols.length; ei++) {
          if (excludedFields[visibleCols[ei].field]) excludedIndices[ei] = true;
        }
        // Remove excluded columns from clone (header + rows)
        var headerCells = clone.querySelectorAll('.gantt-table-header-row .gantt-table-header-cell');
        for (var ri = headerCells.length - 1; ri >= 0; ri--) {
          if (excludedIndices[ri]) headerCells[ri].remove();
        }
        clone.querySelectorAll('.gantt-table-row').forEach(function(row) {
          var cells = row.querySelectorAll('.gantt-table-cell');
          for (var ri2 = cells.length - 1; ri2 >= 0; ri2--) {
            if (excludedIndices[ri2]) cells[ri2].remove();
          }
        });

        // === Auto-expand column widths to fit all content ===
        var measureCanvas = document.createElement('canvas');
        var measureCtx = measureCanvas.getContext('2d');
        var adjustedColumnWidths = [];
        var cellPadding = 24;

        for (var ci = 0; ci < visibleCols.length; ci++) {
          if (excludedIndices[ci]) continue; // salteamos columnas de dependencias
          var col = visibleCols[ci];
          var originalW = self.getEffectiveColumnWidth(col);
          var neededW = col.minWidth || 50;

          var headerText = self.$t(col.textKey) || col.text;
          measureCtx.font = '600 14px sans-serif';
          var headerW = Math.ceil(measureCtx.measureText(headerText).width) + cellPadding;
          neededW = Math.max(neededW, headerW);

          if (col.field === 'percentDone' || col.field === 'latestPlanningProgress') {
            neededW = Math.max(neededW, 32 + cellPadding);
          } else if (col.field === 'status') {
            measureCtx.font = 'bold 9px sans-serif';
            self.sortedVisibleRows.forEach(function(row) {
              if (row.status !== null && row.status !== undefined) {
                var label = self.getStatusLabel(row);
                if (label) {
                  var w = Math.ceil(measureCtx.measureText(label.toUpperCase()).width) + 20 + cellPadding;
                  neededW = Math.max(neededW, w);
                }
              }
            });
          } else if (col.field === 'successors' || col.field === 'predecessors') {
            neededW = Math.max(neededW, originalW);
          } else if (col.isTree) {
            measureCtx.font = '12px sans-serif';
            self.sortedVisibleRows.forEach(function(row) {
              var text = String(row[col.field] || '');
              var indent = (row.level || 0) * 16 + 20;
              var w = Math.ceil(measureCtx.measureText(text).width) + indent + cellPadding;
              neededW = Math.max(neededW, w);
            });
          } else if (col.field === 'duration') {
            measureCtx.font = '12px sans-serif';
            self.sortedVisibleRows.forEach(function(row) {
              var text = self.calculateDuration(row);
              if (text) {
                var w = Math.ceil(measureCtx.measureText(text).width) + cellPadding;
                neededW = Math.max(neededW, w);
              }
            });
          } else if (col.field.includes('date') || col.field.includes('Date')) {
            measureCtx.font = '12px sans-serif';
            self.sortedVisibleRows.forEach(function(row) {
              var text = self.formatDate(row[col.field]);
              if (text) {
                var w = Math.ceil(measureCtx.measureText(text).width) + cellPadding;
                neededW = Math.max(neededW, w);
              }
            });
          } else {
            measureCtx.font = '12px sans-serif';
            self.sortedVisibleRows.forEach(function(row) {
              var text = String(row[col.field] || '');
              if (text) {
                var w = Math.ceil(measureCtx.measureText(text).width) + cellPadding;
                neededW = Math.max(neededW, w);
              }
            });
          }

          adjustedColumnWidths.push(Math.max(originalW, neededW));
        }

        totalColumnsW = adjustedColumnWidths.reduce(function(sum, w) { return sum + w; }, 0);

        var headerCellEls = clone.querySelectorAll('.gantt-table-header-row .gantt-table-header-cell');
        headerCellEls.forEach(function(cell, ci) {
          if (ci < adjustedColumnWidths.length) {
            cell.style.width = adjustedColumnWidths[ci] + 'px';
            cell.style.minWidth = adjustedColumnWidths[ci] + 'px';
          }
        });

        clone.querySelectorAll('.gantt-table-row').forEach(function(row) {
          var cells = row.querySelectorAll('.gantt-table-cell');
          cells.forEach(function(cell, ci) {
            if (ci < adjustedColumnWidths.length) {
              cell.style.width = adjustedColumnWidths[ci] + 'px';
              cell.style.minWidth = adjustedColumnWidths[ci] + 'px';
            }
          });
        });

        var headerRowEl = clone.querySelector('.gantt-table-header-row');
        if (headerRowEl) headerRowEl.style.minWidth = totalColumnsW + 'px';
        clone.querySelectorAll('.gantt-table-row').forEach(function(row) {
          row.style.minWidth = totalColumnsW + 'px';
        });
        var tableContent = clone.querySelector('.gantt-table-content');
        if (tableContent) tableContent.style.minWidth = totalColumnsW + 'px';

        totalW = totalColumnsW + splitterW + timelineW;

        // Remove CSS performance hints that confuse html2canvas rendering
        clone.querySelectorAll('.gantt-table-content, .gantt-chart-content, .gantt-table-body, .gantt-chart-body, .gantt-main, .gantt-left-panel, .gantt-right-panel').forEach(function(el) {
          el.style.contain = 'none';
          el.style.willChange = 'auto';
          el.style.isolation = 'auto';
        });
        clone.style.contain = 'none';
        clone.style.willChange = 'auto';
        clone.style.isolation = 'auto';

        // === Layout: container ===
        clone.style.width = totalW + 'px';
        clone.style.height = fullHeight + 'px';
        clone.style.overflow = 'hidden';

        var main = clone.querySelector('.gantt-main');
        if (main) {
          main.style.height = fullHeight + 'px';
          main.style.flex = 'none';
          main.style.overflow = 'hidden';
        }

        var lp = clone.querySelector('.gantt-left-panel');
        if (lp) {
          lp.style.width = totalColumnsW + 'px';
          lp.style.height = fullHeight + 'px';
          lp.style.overflow = 'hidden';
        }

        var thWrapper = clone.querySelector('.gantt-table-header-wrapper');
        if (thWrapper) {
          thWrapper.style.overflow = 'visible';
          thWrapper.style.width = totalColumnsW + 'px';
          thWrapper.scrollLeft = 0;
        }
        var tableHeaderInner = clone.querySelector('.gantt-table-header');
        if (tableHeaderInner) {
          tableHeaderInner.style.transform = 'none';
        }

        var lb = clone.querySelector('.gantt-table-body');
        if (lb) {
          lb.style.height = (rowsHeight + rh) + 'px';
          lb.style.overflow = 'visible';
          lb.style.flex = 'none';
          lb.style.width = totalColumnsW + 'px';
          lb.scrollLeft = 0;
          lb.scrollTop = 0;
        }

        var rp = clone.querySelector('.gantt-right-panel');
        if (rp) {
          rp.style.width = timelineW + 'px';
          rp.style.height = fullHeight + 'px';
          rp.style.overflow = 'hidden';
          rp.style.flex = 'none';
        }

        var timeHeaderWrap = clone.querySelector('.gantt-time-header-wrapper');
        if (timeHeaderWrap) {
          timeHeaderWrap.style.overflow = 'hidden';
          timeHeaderWrap.style.width = timelineW + 'px';
        }
        var timeHeader = clone.querySelector('.gantt-time-header');
        if (timeHeader) {
          timeHeader.style.marginLeft = (-captureStartX) + 'px';
        }

        var rb = clone.querySelector('.gantt-chart-body');
        if (rb) {
          rb.style.height = (rowsHeight + rh) + 'px';
          rb.style.overflow = 'hidden';
          rb.style.flex = 'none';
          rb.style.width = timelineW + 'px';
        }
        var chartContent = clone.querySelector('.gantt-chart-content');
        if (chartContent) {
          chartContent.style.marginLeft = (-captureStartX) + 'px';
        }

        // === Fix flex layout for html2canvas ===
        clone.querySelectorAll('.gantt-table-header-row').forEach(function(row) {
          row.style.display = 'block';
          row.style.whiteSpace = 'nowrap';
          row.style.fontSize = '0';
          row.style.lineHeight = headerHeight + 'px';
        });
        clone.querySelectorAll('.gantt-table-header-cell').forEach(function(cell) {
          cell.style.display = 'inline-block';
          cell.style.verticalAlign = 'middle';
          cell.style.fontSize = '14px';
          cell.style.lineHeight = headerHeight + 'px';
          cell.style.height = headerHeight + 'px';
          cell.style.boxSizing = 'border-box';
        });
        clone.querySelectorAll('.gantt-table-header-cell > *').forEach(function(child) {
          child.style.display = 'inline-block';
          child.style.verticalAlign = 'middle';
          child.style.lineHeight = 'normal';
        });

        var timeHeaderEl = clone.querySelector('.gantt-time-header');
        if (timeHeaderEl) {
          timeHeaderEl.style.display = 'block';
        }
        var timeRows = clone.querySelectorAll('.gantt-time-header-row');
        var timeRowCount = timeRows.length;
        if (timeRowCount > 0) {
          var perRowH = headerHeight / timeRowCount;
          timeRows.forEach(function(row) {
            row.style.display = 'block';
            row.style.height = perRowH + 'px';
            row.style.flex = 'none';
          });
          clone.querySelectorAll('.gantt-time-header-cell').forEach(function(cell) {
            cell.style.display = 'block';
            cell.style.textAlign = 'center';
            cell.style.lineHeight = perRowH + 'px';
          });
        }
        clone.querySelectorAll('.sticky-label').forEach(function(el) {
          el.style.position = 'static';
        });

        clone.querySelectorAll('.gantt-table-row').forEach(function(row) {
          row.style.display = 'block';
          row.style.whiteSpace = 'nowrap';
          row.style.lineHeight = rh + 'px';
          row.style.fontSize = '0';
        });

        clone.querySelectorAll('.gantt-table-cell').forEach(function(cell) {
          cell.style.display = 'inline-block';
          cell.style.verticalAlign = 'middle';
          cell.style.height = rh + 'px';
          cell.style.lineHeight = rh + 'px';
          cell.style.boxSizing = 'border-box';
          cell.style.overflow = 'hidden';
          cell.style.fontSize = '12px';
          cell.style.whiteSpace = 'nowrap';
        });

        // === Status badges: explicit pill styling ===
        clone.querySelectorAll('.status-badge').forEach(function(el) {
          el.style.display = 'inline-block';
          el.style.verticalAlign = 'middle';
          el.style.padding = '0px 8px';
          el.style.borderRadius = '4px';
          el.style.fontSize = '9px';
          el.style.fontWeight = 'bold';
          el.style.textTransform = 'uppercase';
          el.style.whiteSpace = 'nowrap';
          el.style.lineHeight = '14px';
          el.style.height = 'auto';
          el.style.width = 'auto';
          el.style.color = '#fff';
          el.style.boxSizing = 'border-box';
        });

        // === Dependency badges: explicit pill styling ===
        clone.querySelectorAll('.dependency-badges').forEach(function(el) {
          el.style.display = 'inline-block';
          el.style.verticalAlign = 'middle';
          el.style.lineHeight = 'normal';
          el.style.whiteSpace = 'nowrap';
          el.style.overflow = 'visible';
        });
        clone.querySelectorAll('.dependency-badge').forEach(function(el) {
          el.style.display = 'inline-block';
          el.style.verticalAlign = 'middle';
          el.style.padding = '0px 8px';
          el.style.borderRadius = '12px';
          el.style.fontSize = '11px';
          el.style.whiteSpace = 'nowrap';
          el.style.lineHeight = '16px';
          el.style.height = 'auto';
          el.style.background = '#e1e5e8';
          el.style.color = '#555';
          el.style.marginRight = '4px';
          el.style.boxSizing = 'border-box';
        });

        // === Progress circle ===
        clone.querySelectorAll('.progress-circle').forEach(function(el) {
          el.style.display = 'inline-block';
          el.style.verticalAlign = 'middle';
          el.style.width = '32px';
          el.style.height = '32px';
          el.style.lineHeight = 'normal';
        });

        // Vertical alignment fixes for html2canvas
        clone.querySelectorAll('.gantt-table-cell').forEach(function (item) {
          item.style.position = 'relative';
        });
        clone.querySelectorAll('.gantt-table-cell > *').forEach(function (child) {
          child.style.position = 'absolute';
          child.style.top = '50%';
          child.style.transform = 'translateY(-50%)';
        });

        clone.querySelectorAll('.gantt-table-cell.cell-centered > *').forEach(function(child) {
          child.style.left = '50%';
          child.style.transform = 'translate(-50%, -50%)';
        });
        clone.querySelectorAll('.status-badge, .dependency-badge').forEach(function(child) {
          child.style.paddingBottom = '10px';
        });

        clone.querySelectorAll('.gantt-table-header-cell.header-centered').forEach(function(cell) {
          cell.style.textAlign = 'center';
        });

        clone.querySelectorAll('.task-label').forEach(function (child) {
          child.style.paddingBottom = '10px';
        });
        clone.querySelectorAll('.gantt-table-header-cell span').forEach(function (child) {
          child.style.paddingBottom = '15px';
        });
        clone.querySelectorAll('.gantt-table-header-cell i').forEach(function (child) {
          child.style.display = 'none';
        });
        clone.querySelectorAll('.sticky-label').forEach(function (child) {
          child.style.paddingBottom = '10px';
        });
        clone.querySelectorAll('.progress-text').forEach(function (child) {
          child.style.paddingBottom = '10px';
        });
        clone.querySelectorAll('.task-label-outside').forEach(function (child) {
          child.style.lineHeight = '8px';
        });

        clone.querySelectorAll('.gantt-time-header-cell > *').forEach(function (child) {
          child.style.top = '-20%';
          child.style.position = 'absolute';
        });

        // Wait for browser to compute layout on the clone
        await new Promise(resolve => setTimeout(resolve, 150));

        // Cap dimensions and scale to avoid exceeding max canvas size
        var maxCanvas = 16000;
        fullHeight = Math.min(fullHeight, maxCanvas);
        totalW = Math.min(totalW, maxCanvas);
        var autoScale = Math.min(2, Math.floor(maxCanvas / Math.max(totalW, fullHeight)));
        autoScale = Math.max(1, autoScale);

        const canvas = await html2canvas(clone, {
          width: totalW,
          height: fullHeight,
          backgroundColor: '#ffffff',
          scale: autoScale,
        });

        document.body.removeChild(wrapper);

        // Download the image
        const link = document.createElement('a');
        const projectName = this.project ? this.project.name : 'gantt';
        const date = moment().format('YYYY-MM-DD_HHmmss');
        link.download = `${projectName}_gantt_${date}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();

        // Guardamos una versión comprimida para acceso rápido desde el exportador de informes
        this.saveGanttCapture(canvas, moment().format('YYYY-MM-DD HH:mm:ss'));
      } catch (error) {
        console.error('Screenshot capture failed:', error);
      } finally {
        this.isCapturing = false;
        this.selectedRowId = savedSelectedRowId;
      }
    },

    // Comprime el canvas del screenshot y lo guarda en IndexedDB (máx 3 por proyecto, FIFO)
    saveGanttCapture(canvas, date) {
      try {
        var projectId = this.project ? this.project.id : 'unknown';

        // Reducimos a un máximo de 10000px manteniendo proporción.
        var maxDim = 10000;
        var scale = Math.min(1, maxDim / Math.max(canvas.width, canvas.height));
        var compCanvas = document.createElement('canvas');
        compCanvas.width = Math.round(canvas.width * scale);
        compCanvas.height = Math.round(canvas.height * scale);
        compCanvas.getContext('2d').drawImage(canvas, 0, 0, compCanvas.width, compCanvas.height);

        // Usamos WebP: para las líneas y el texto del Gantt da más nitidez que
        // JPEG y pesa bastante menos. Guardamos el Blob binario en IndexedDB,
        // manteniendo un máximo de 3 capturas por proyecto (FIFO).
        compCanvas.toBlob(function (blob) {
          if (!blob) return;
          saveToIndexedDb('gantt_captures:' + projectId, { date: date, blob: blob }, 3).catch(function (e) {
            console.warn('No se pudo guardar la captura del Gantt en IndexedDB:', e);
          });
        }, 'image/webp', 0.95);
      } catch (e) {
        console.warn('No se pudo generar la captura del Gantt:', e);
      }
    },

    // =========================================================================
    // Fullscreen
    // =========================================================================

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.$refs.ganttContainer.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }
  }
};
